/* eslint-disable react/display-name */
import { TableColumnType } from 'antd'
import { ColDef } from 'ag-grid-community'
import moment from 'moment'
import React from 'react'
import { getStationOrderSourceNames } from '../../../models/changeWater'
import { DateTimeFormatSimple } from '../../../models/datetime'
import { Dimen } from '../../../models/dimen'
import styles from './charge.module.css'

// ==================== 静态列元数据（供列显隐/拖拽/持久化使用） ====================
// 固定列（序号/就诊序号/姓名 左固定，结算编号/操作 右固定）不可拖拽、不可隐藏
export const waterListData: any[] = [
  {
    headerName: '序号',
    field: 'index',
    width: 64,
    minWidth: 64,
    pinned: 'left',
    lockPosition: 'left',
    suppressMovable: true,
    visible: true,
  },
  {
    headerName: '就诊序号',
    field: 'outpatientNo',
    width: 96,
    minWidth: 96,
    pinned: 'left',
    lockPosition: 'left',
    suppressMovable: true,
    visible: true,
  },
  {
    headerName: '姓名',
    field: 'patientName',
    width: 96,
    minWidth: 96,
    pinned: 'left',
    lockPosition: 'left',
    suppressMovable: true,
    visible: true,
  },
  {
    headerName: '险种代码',
    field: 'insuranceCode',
    width: 96,
    minWidth: 96,
    visible: true,
  },
  {
    headerName: '就诊类别',
    field: 'orderSource',
    width: 96,
    minWidth: 96,
    visible: true,
  },
  {
    headerName: '总金额',
    field: 'totalPayAmount',
    width: 128,
    minWidth: 128,
    visible: true,
  },
  {
    headerName: '应收金额',
    field: 'shouldPayAmount',
    width: 128,
    minWidth: 128,
    visible: true,
  },
  {
    headerName: '实收金额',
    field: 'realPayAmount',
    width: 128,
    minWidth: 128,
    visible: true,
  },
  {
    headerName: '发生人员',
    field: 'operateUserName',
    width: 128,
    minWidth: 128,
    visible: true,
  },
  {
    headerName: '操作时间',
    field: 'createTime',
    width: 128,
    minWidth: 128,
    visible: true,
  },
  {
    headerName: '结算编号',
    field: 'paymentId',
    width: 128,
    minWidth: 128,
    pinned: 'right',
    lockPosition: 'right',
    suppressMovable: true,
    visible: true,
  },
  {
    headerName: '操作',
    field: 'action',
    width: 128,
    minWidth: 128,
    pinned: 'right',
    lockPosition: 'right',
    suppressMovable: true,
    visible: true,
  },
]?.map((v, index) => {
  return {
    ...v,
    index: index + 1,
  }
})

// ==================== 动态支付列（随数据变化，参与列显隐/拖拽/持久化） ====================
// field 使用「支付方式名 + 行内下标」作为稳定标识，参与列配置持久化
export const buildDynamicColumns = (data?: any): any[] => {
  const customizePayList = data?.[0]?.customizePay
    ?.map((k: any, index: number) => {
      return { ...k, itemIndex: index }
    })
    ?.sort((a: any, b: any) => a.payMethodName.localeCompare(b.payMethodName))
  return (customizePayList || [])?.map((iv: any, i: number) => {
    return {
      headerName: iv?.payMethodName,
      field: 'otherPay_' + iv?.payMethodName + '_' + iv?.itemIndex,
      payMethodName: iv?.payMethodName,
      itemIndex: iv?.itemIndex,
      width: 96,
      minWidth: 96,
      visible: true, // 默认显示，显隐状态随配置持久化
      dynamic: true, // 动态列标记
      index: 1000 + i, // 排序用，位于静态列之后
    }
  })
}

// ==================== AG Grid 列渲染器 ====================
export const AGWaterColumns = (
  startIndex?: any,
  onclick?: (id: any, t: any, state: any) => void,
  columnList?: any
): ColDef[] => {
  const columns: any[] = (columnList || waterListData)?.map((item: any) => {
    // 动态支付列：按「支付方式名 + 行内下标」匹配取值
    if (item?.dynamic) {
      return {
        ...item,
        headerClass: styles.rightAligned,
        cellStyle: { textAlign: 'right' },
        cellRendererFramework: (params: any) => {
          if (params.node.rowPinned) {
            return params.value || ''
          }
          const r = params.data
          const payList = (r?.customizePay || [])?.map(
            (vv: any, vi: number) => ({
              ...vv,
              itemIndex: vi,
            })
          )
          const matched =
            payList?.find(
              (vv: any) =>
                vv.payMethodName == item?.payMethodName &&
                vv.itemIndex === item?.itemIndex
            ) ||
            payList?.find(
              (vv: any) => vv.payMethodName == item?.payMethodName
            )
          return matched ? Number(matched?.payMethodAmount)?.toFixed(2) : ''
        },
      }
    }
    switch (item.field) {
      case 'index':
        return {
          ...item,
          cellStyle: { textAlign: 'center' },
          cellRendererFramework: (params: any) => {
            if (params.node.rowPinned) return ''
            return `${Number(startIndex || 0) + params.node.rowIndex + 1}`
          },
        }
      case 'patientName':
        return {
          ...item,
          cellStyle: { textAlign: 'center' },
          cellRendererFramework: (params: any) => {
            if (params.node.rowPinned) return '合计'
            return params.value ? params.value : '-'
          },
        }
      case 'insuranceCode':
        return {
          ...item,
          cellStyle: { textAlign: 'center' },
          cellRendererFramework: (params: any) => {
            if (params.node.rowPinned) return ''
            return params.value !== 'ZIFEI'
              ? '医保'
              : '自费'
          },
        }
      case 'orderSource':
        return {
          ...item,
          cellStyle: { textAlign: 'center' },
          cellRendererFramework: (params: any) => {
            if (params.node.rowPinned) return ''
            return getStationOrderSourceNames(params.value)
          },
        }
      case 'totalPayAmount':
      case 'shouldPayAmount':
      case 'realPayAmount':
        return {
          ...item,
          headerClass: styles.rightAligned,
          cellStyle: { textAlign: 'right' },
          cellRendererFramework: (params: any) => {
            if (params.node.rowPinned) {
              return params.value || ''
            }
            const r = params.data
            if (r?.recordType == 1 && item.field != 'realPayAmount') {
              return '-'
            }
            return Number(params.value)?.toFixed(2)
          },
        }
      case 'operateUserName':
      case 'paymentId':
        return {
          ...item,
          cellStyle: { textAlign: 'center' },
          cellRendererFramework: (params: any) => {
            if (params.node.rowPinned) return ''
            return params.value ? params.value : '-'
          },
        }
      case 'createTime':
        return {
          ...item,
          cellStyle: { textAlign: 'center' },
          cellRendererFramework: (params: any) => {
            if (params.node.rowPinned) return ''
            return params.value ? params.value.substring(0, 10) : '-'
          },
        }
      case 'action':
        return {
          ...item,
          cellStyle: { textAlign: 'center' },
          cellRendererFramework: function columnAction(params: any) {
            if (params.node.rowPinned) return ''
            return (
              <a
                type='primary'
                onClick={() => {
                  onclick && onclick(params.data.id, '查看详情', params.data.state)
                }}
              >
                查看详情
              </a>
            )
          },
        }
      default:
        return {
          ...item,
          cellStyle: { textAlign: 'center' },
        }
    }
  })
  return columns || []
}

export const PrintChangeWaterColumns = (
  onclick: (t: string, id: any, state: number) => void,
  startIndex: number
): TableColumnType<any>[] => {
  return [
    {
      title: '序号',
      key: 'no',
      width: '3rem',
      align: 'center',
      render: (_1, _2, index) => `${startIndex + index + 1}`,
    },
    {
      title: '就诊序号',
      dataIndex: 'outpatientNo',
      width: '4rem',
      align: 'center',
      key: 'outpatientNo',
      render: (_: any, t: any) => {
        return t.outpatientNo == -1 ? ' ' : t.outpatientNo
      },
    },
    {
      title: '姓名',
      dataIndex: 'patientName',
      align: 'center',
      key: 'patientName',
    },
    {
      title: '结算险种',
      dataIndex: 'insuranceCode',
      width: '10em',
      align: 'center',
      ellipsis: true,
      render: (_, t) => {
        return t.insuranceCode === 'ZIFEI' || !t.insuranceCode || !t
            ? '自费'
            : '智慧医保'
      },
    },
    {
      title: '就诊类别',
      dataIndex: 'orderSource',
      width: '6rem',
      align: 'center',
      key: 'orderSource',
      render: (_, t) => {
        return getStationOrderSourceNames(t.orderSource)
      },
    },
    // {
    //   title: "发生类型",
    //   dataIndex: "state",
    //   align: "center",
    //   render: (state: number) => {
    //     return state > 0 ? "已退款" : "已付款";
    //   },
    // },
    {
      title: '支付方式',
      dataIndex: 'payMethodName',
      width: Dimen.PayMethod,
      align: 'center',
      key: 'payMethodName',
      // render: (_, t) => {
      //   return getPayMethodName(t.payMethod)
      // },
    },
    {
      title: '总金额',
      dataIndex: 'totalPayAmount',
      width: Dimen.Price,
      align: 'right',
      key: 'totalPayAmount',
      render: (_, r) => r.totalPayAmount?.toFixed(2),
    },
    // {
    //   title: '现金金额',
    //   dataIndex: 'receivePayAmount',
    //   width: Dimen.Price,
    //   align: 'right',
    //   key: 'receivePayAmount',
    //   render: (_, r) => r.receivePayAmount?.toFixed(2),
    // },
    {
      title: '应收金额',
      dataIndex: 'shouldPayAmount',
      width: Dimen.Price,
      align: 'right',
      key: 'shouldPayAmount',
      render: (_, r) => r.shouldPayAmount?.toFixed(2),
    },
    {
      title: '实收金额',
      dataIndex: 'realPayAmount',
      width: Dimen.Price,
      align: 'right',
      key: 'realPayAmount',
      render: (_, r) => r.realPayAmount?.toFixed(2),
    },
    // {
    //   title: '现金金额',
    //   dataIndex: 'realPayAmount',
    //   width: Dimen.Price,
    //   align: 'right',
    //   key: 'realPayAmount',
    //   render: (_, r) => r.realPayAmount?.toFixed(2),
    // },
    // {
    //   title: '医保金额',
    //   dataIndex: 'realPayAmount',
    //   width: Dimen.Price,
    //   align: 'right',
    //   key: 'realPayAmount',
    //   render: (_, r) => r.realPayAmount?.toFixed(2),
    // },

    {
      title: '发生人员',
      dataIndex: 'operateUserName',
      width: Dimen.Name,
      align: 'center',
      key: 'operateUserName',
    },
    {
      title: '操作时间',
      dataIndex: 'createTime',
      width: Dimen.Time,
      align: 'center',
      key: 'createTime',
      render: (_, t) => {
        return t.createTime
          ? moment(t.createTime)?.format(DateTimeFormatSimple)
          : '-'
      },
    },
    {
      title: '结算编号',
      dataIndex: 'paymentId',
      width: Dimen.PaymentId,
      align: 'center',
      key: 'paymentId',
    },
  ]
}
